import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // Return default values if used outside AuthProvider
    return {
      user: null,
      loading: false,
      error: null,
      isAuthenticated: false,
      isSuperAdmin: false,
      organizations: [],
      currentOrganization: null,
      needsOrgSelection: false,
      login: async () => ({ success: false }),
      logout: () => {},
      clearError: () => {},
      selectOrganization: () => {},
      switchOrganization: () => {},
    };
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [needsOrgSelection, setNeedsOrgSelection] = useState(false);

  // Load organization from localStorage
  const loadOrganization = useCallback(() => {
    const savedOrgId = localStorage.getItem('current_organization_id');
    const savedOrg = localStorage.getItem('current_organization');
    if (savedOrgId && savedOrg) {
      try {
        setCurrentOrganization(JSON.parse(savedOrg));
      } catch (e) {
        localStorage.removeItem('current_organization_id');
        localStorage.removeItem('current_organization');
      }
    }
  }, []);

  // Fetch user's organizations
  const fetchOrganizations = useCallback(async () => {
    try {
      const orgs = await authAPI.getMyOrganizations();
      setOrganizations(orgs);
      return orgs;
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      return [];
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('admin_user');

      if (token && savedUser) {
        try {
          // Verify token is still valid
          await authAPI.verifyToken();
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);

          // If not superadmin, load organizations
          if (!parsedUser.is_superadmin) {
            loadOrganization();
            const orgs = await fetchOrganizations();

            // Check if needs org selection
            const savedOrgId = localStorage.getItem('current_organization_id');
            if (!savedOrgId && orgs.length > 0) {
              if (orgs.length === 1) {
                // Auto-select single org
                selectOrganization(orgs[0]);
              } else {
                setNeedsOrgSelection(true);
              }
            }
          }
        } catch (err) {
          // Token invalid, clear storage
          localStorage.removeItem('auth_token');
          localStorage.removeItem('admin_user');
          localStorage.removeItem('current_organization_id');
          localStorage.removeItem('current_organization');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [loadOrganization, fetchOrganizations]);

  const login = async (username, password) => {
    setError(null);
    try {
      // Get token
      const tokenData = await authAPI.login(username, password);
      localStorage.setItem('auth_token', tokenData.access_token);

      // Get user info
      const userData = await authAPI.getCurrentAdmin();
      localStorage.setItem('admin_user', JSON.stringify(userData));
      setUser(userData);

      // If superadmin, no org needed
      if (userData.is_superadmin) {
        return { success: true, isSuperAdmin: true };
      }

      // Fetch user's organizations
      const orgs = await fetchOrganizations();

      if (orgs.length === 0) {
        return { success: true, noOrganizations: true };
      } else if (orgs.length === 1) {
        // Auto-select single org
        selectOrganization(orgs[0]);
        return { success: true };
      } else {
        // Multiple orgs - need selection
        setNeedsOrgSelection(true);
        return { success: true, needsOrgSelection: true };
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const selectOrganization = (org) => {
    localStorage.setItem('current_organization_id', org.id);
    localStorage.setItem('current_organization', JSON.stringify(org));
    setCurrentOrganization(org);
    setNeedsOrgSelection(false);
  };

  const switchOrganization = async (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      selectOrganization(org);
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('current_organization_id');
    localStorage.removeItem('current_organization');
    setUser(null);
    setOrganizations([]);
    setCurrentOrganization(null);
    setNeedsOrgSelection(false);
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isSuperAdmin: user?.is_superadmin || false,
    organizations,
    currentOrganization,
    needsOrgSelection,
    login,
    logout,
    clearError: () => setError(null),
    selectOrganization,
    switchOrganization,
    refreshOrganizations: fetchOrganizations,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
